/**
 * DrillTodayPage — Daily spaced repetition drill runner.
 *
 * Route: /drill/today
 *
 * EPIC-08-12: Presents 5–10 AI-generated questions, collects answers,
 * and submits to POST /api/v1/drills/today/submit for scoring.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import styles from './DrillTodayPage.module.css';

interface DrillQuestion {
  question: string;
  type: 'mcq' | 'true_false' | 'scenario';
  options: string[] | null;
  correct_answer: string;
  explanation: string;
}

interface DrillState {
  status: 'available' | 'completed' | 'locked';
  questions: DrillQuestion[] | null;
}

interface SubmitResult {
  score: number;
  correct_count: number;
  total_count: number;
  summary: string;
}

export function DrillTodayPage(): JSX.Element {
  const { t } = useTranslation('training');
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<DrillQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [currentQ, setCurrentQ] = useState(0);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'available' | 'completed' | 'locked'>('available');

  useEffect(() => {
    apiClient
      .get<{ data: DrillState }>('/drills/today')
      .then((res) => {
        const d = res.data.data;
        setStatus(d.status);
        if (d.questions) setQuestions(d.questions);
      })
      .finally(() => setIsLoading(false));
  }, []);

  function handleAnswer(value: string): void {
    setAnswers((prev) => ({ ...prev, [currentQ]: value }));
  }

  function handleNext(): void {
    if (currentQ < questions.length - 1) {
      setCurrentQ((q) => q + 1);
    }
  }

  async function handleSubmit(): Promise<void> {
    setIsSubmitting(true);
    const payload = Object.entries(answers).map(([i, answer]) => ({
      question_index: Number(i),
      answer,
    }));
    try {
      const res = await apiClient.post<{ data: SubmitResult }>('/drills/today/submit', {
        answers: payload,
      });
      setResult(res.data.data);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return <main className={styles.page}><p>{t('common.loading')}</p></main>;
  }

  if (status === 'locked') {
    return (
      <main className={styles.page}>
        <button className={styles.back} onClick={() => navigate('/learn')}>← {t('common.button.back')}</button>
        <p className={styles.lockedMsg}>{t('drill.lockedMessage')}</p>
      </main>
    );
  }

  if (status === 'completed' || result) {
    const pct = result ? Math.round(result.score) : 0;
    const circumference = 2 * Math.PI * 52;
    const dash = (pct / 100) * circumference;
    const pass = pct >= 70;
    return (
      <main className={styles.page}>
        <button className={styles.back} onClick={() => navigate('/learn')}>← {t('common.button.back')}</button>
        <div className={styles.resultCard}>
          {result ? (
            <>
              <div className={styles.scoreRing}>
                <svg width="140" height="140" viewBox="0 0 140 140">
                  <circle cx="70" cy="70" r="52" fill="none" stroke="#e8eaf2" strokeWidth="12" />
                  <circle
                    cx="70" cy="70" r="52" fill="none"
                    stroke={pass ? '#1a7a4a' : '#c95f00'}
                    strokeWidth="12" strokeLinecap="round"
                    strokeDasharray={`${dash} ${circumference}`}
                    transform="rotate(-90 70 70)"
                  />
                  <text x="70" y="66" textAnchor="middle" fontSize="30" fontWeight="800" fill="#1a1a2e">{pct}%</text>
                  <text x="70" y="88" textAnchor="middle" fontSize="12" fontWeight="600" fill="#7a7a9a">
                    {result.correct_count}/{result.total_count}
                  </text>
                </svg>
              </div>
              <h1 className={styles.resultTitle}>{t('drill.result.title')}</h1>
              <p className={styles.summary}>{result.summary}</p>
              <button className={styles.submitBtn} onClick={() => navigate('/learn')} type="button">
                {t('drill.result.done', 'Back to Learning')}
              </button>
            </>
          ) : (
            <>
              <div className={styles.resultIcon} aria-hidden="true">✅</div>
              <h1 className={styles.resultTitle}>{t('drill.result.title')}</h1>
              <p className={styles.summary}>{t('drill.completedMessage')}</p>
            </>
          )}
        </div>
      </main>
    );
  }

  const q = questions[currentQ];
  if (!q) return <main className={styles.page}><p>{t('common.loading')}</p></main>;

  const isLast = currentQ === questions.length - 1;
  const hasAnswer = !!answers[currentQ];
  const allAnswered = questions.every((_, i) => !!answers[i]);

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <button className={styles.back} onClick={() => navigate('/learn')}>← {t('common.button.back')}</button>
        <div className={styles.headerTitleRow}>
          <span className={styles.drillBadge}>🎯 {t('drill.title', "Today's Drill")}</span>
          <span className={styles.progress}>
            {t('drill.questionOf', { current: currentQ + 1, total: questions.length })}
          </span>
        </div>
        <div className={styles.stepper} role="progressbar" aria-valuenow={currentQ + 1} aria-valuemin={1} aria-valuemax={questions.length}>
          {questions.map((_, i) => (
            <span
              key={i}
              className={`${styles.step} ${i < currentQ ? styles.stepDone : ''} ${i === currentQ ? styles.stepCurrent : ''} ${answers[i] ? styles.stepAnswered : ''}`}
            />
          ))}
        </div>
      </header>

      <section className={styles.questionCard} aria-label={t('drill.questionLabel')}>
        <p className={styles.questionText}>{q.question}</p>

        {q.type !== 'scenario' && q.options ? (
          <div className={styles.options}>
            {q.options.map((opt, i) => {
              const selected = answers[currentQ] === opt;
              return (
                <button
                  key={opt}
                  className={`${styles.option} ${selected ? styles.selected : ''}`}
                  onClick={() => handleAnswer(opt)}
                  type="button"
                >
                  <span className={styles.optionLetter}>{String.fromCharCode(65 + i)}</span>
                  <span className={styles.optionText}>{opt.replace(/^\s*[a-dA-D]\)\s*/, '')}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <textarea
            className={styles.scenarioInput}
            value={answers[currentQ] ?? ''}
            onChange={(e) => handleAnswer(e.target.value)}
            placeholder={t('drill.scenarioPlaceholder')}
            rows={3}
            aria-label={t('drill.scenarioLabel')}
          />
        )}
      </section>

      <div className={styles.actions}>
        {!isLast && (
          <button
            className={styles.nextBtn}
            onClick={handleNext}
            disabled={!hasAnswer}
          >
            {t('drill.nextButton')}
          </button>
        )}
        {isLast && (
          <button
            className={styles.submitBtn}
            onClick={() => void handleSubmit()}
            disabled={!allAnswered || isSubmitting}
          >
            {isSubmitting ? t('drill.submitting') : t('drill.submitButton')}
          </button>
        )}
      </div>
    </main>
  );
}
